import { capitalise } from '@upheal/design-system/utils/stringUtils';
import { BillingPeriodType } from './billingPeriod';

const TIER_PERIOD_SEPARATOR = '-';

export const PricingTier = {
    FREE: 'free',
    STARTER: 'starter',
    PREMIUM: 'premium',
    TRIAL: 'free-trial',
    ENTERPRISE: 'enterprise',
    METERED: 'metered',
    // Team plan
    GROUP: 'group',
    // legacy plan
    UNLIMITED: 'unlimited',
} as const;

export type PricingTier = (typeof PricingTier)[keyof typeof PricingTier];

export const POPULAR_TIER = PricingTier.PREMIUM;

export const LOWEST_PAID_TIER = PricingTier.STARTER;
export const LOWEST_PAID_TIER_WITH_UPHEAL_OS = PricingTier.PREMIUM;

/**
 * Some plans are only identified by their tier, others also include billing cycle info.
 * @example
 * 'premium-yearly' -> { pricingTier: 'premium', isYearly: true }
 * 'starter-monthly' -> { pricingTier: 'starter', isYearly: false }
 * 'free' -> { pricingTier: 'free', isYearly: false }
 */
export const parsePlanId = (planId: string) => {
    const pricingTier = planId.split(TIER_PERIOD_SEPARATOR)[0] as PricingTier;
    const isYearly = planId.includes(BillingPeriodType.Yearly);

    return {
        pricingTier,
        isYearly,
    };
};

export const isPlan = (planId: string | undefined, tier: PricingTier) =>
    !!planId && parsePlanId(planId).pricingTier === tier;

export const isFreePlan = (planId: string | undefined) =>
    !!planId && planId === PricingTier.FREE;

export const isTrialPlan = (planId: string | undefined) =>
    !!planId && planId === PricingTier.TRIAL;

export const isFreeOrTrialPlan = (planId: string | undefined) =>
    isTrialPlan(planId) || isFreePlan(planId);

export const isPaidPlan = (planId: string | undefined) =>
    !!planId && !isFreeOrTrialPlan(planId);

export const isStarterPlan = (planId: string | undefined) =>
    isPlan(planId, PricingTier.STARTER);

export const isPremiumPlan = (planId: string | undefined) =>
    isPlan(planId, PricingTier.PREMIUM);

export const isEnterprisePlan = (planId: string | undefined) =>
    isPlan(planId, PricingTier.ENTERPRISE);

export const isGroupPlan = (planId: string | undefined) =>
    isPlan(planId, PricingTier.GROUP);

export const isMeteredPlan = (planId: string | undefined) =>
    isPlan(planId, PricingTier.METERED);

export const isUsageBasedPlan = (planId: string | undefined) =>
    isGroupPlan(planId) || isMeteredPlan(planId);

export const isValidTier = (tier: string): tier is PricingTier => {
    return Object.values(PricingTier).includes(tier);
};

export const createYearlyPlanId = (currentPlanId: string) => {
    return `${currentPlanId}-${BillingPeriodType.Yearly}`;
};

export const getPlanDisplayName = (tier: PricingTier) => capitalise(tier);

export const isUphealOSEligiblePlan = (planId: string | undefined) => {
    return (
        isEnterprisePlan(planId) ||
        isPlan(planId, PricingTier.UNLIMITED) ||
        isPlan(planId, LOWEST_PAID_TIER_WITH_UPHEAL_OS) ||
        isGroupPlan(planId)
    );
};
